import React, { useMemo } from 'react';
import { Modal, Table, Button, Tag, Typography, Input } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCardDetail } from '../../context/CardDetailContext';

const { Text } = Typography;

interface NoScheduleCardModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  cards: any[];
}

function formatCardNumber(cardNumber?: string) {
  if (!cardNumber || cardNumber.length < 4) return cardNumber || '';
  return `***${cardNumber.slice(-4)}`;
}

const NoScheduleCardModal: React.FC<NoScheduleCardModalProps> = ({
  open,
  onClose,
  loading,
  cards,
}) => {
  const navigate = useNavigate();
  const { setSelectedCard } = useCardDetail();

  // Tạo filter options cho các cột select
  const bankOptions = useMemo(() => {
    const banks = Array.from(
      new Set(cards.map((card) => card.bank?.name).filter(Boolean))
    );
    return banks.map((name) => ({ text: name, value: name }));
  }, [cards]);

  const statusOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(cards.map((card) => card.status).filter(Boolean))
    );
    return statuses.map((status) => ({ text: status, value: status }));
  }, [cards]);

  const columns = [
    {
      title: 'Số thẻ',
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        record.cardNumber?.toLowerCase().includes(value.toLowerCase()),
      sorter: (a: any, b: any) => a.cardNumber.localeCompare(b.cardNumber),
      render: (cardNumber: string) => formatCardNumber(cardNumber),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: 'cardHolderName',
      key: 'cardHolderName',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm chủ thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        record.cardHolderName?.toLowerCase().includes(value.toLowerCase()),
      sorter: (a: any, b: any) =>
        a.cardHolderName?.localeCompare(b.cardHolderName),
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: 'Ngân hàng',
      dataIndex: ['bank', 'name'],
      key: 'bank',
      filters: bankOptions,
      onFilter: (value: any, record: any) => record.bank?.name === value,
      render: (name: string, record: any) => (
        <Tag color="geekblue">{record.bank?.name || 'Ngân hàng?'}</Tag>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Lưu thẻ', value: 'LUU_THE' },
        { text: 'Trả thẻ', value: 'TRA_THE' },
      ],
      onFilter: (value: any, record: any) => record.status === value,
      render: (status: string) => {
        let color = '';
        let text = '';
        switch (status) {
          case 'LUU_THE':
            color = 'blue';
            text = 'Lưu thẻ';
            break;
          case 'TRA_THE':
            color = 'green';
            text = 'Trả thẻ';
            break;
          default:
            return null;
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },

    {
      title: 'HSD',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      sorter: (a: any, b: any) =>
        (a.expiryDate || '').localeCompare(b.expiryDate || ''),
      render: (date: string) => (date ? <Tag color="red">{date}</Tag> : null),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (notes: string) =>
        notes ? <Text type="secondary">{notes}</Text> : null,
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right' as const,
      width: 90,
      render: (_: any, record: any) => (
        <Button
          icon={<EyeOutlined />}
          type="link"
          onClick={() => {
            setSelectedCard(record);
            navigate('/card-detail');
          }}
        >
          Xem
        </Button>
      ),
    },
  ];

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ fontWeight: 600, fontSize: 18 }}>
          Danh sách thẻ chưa có lịch đáo
        </span>
      }
      footer={null}
      width={1100}
      bodyStyle={{ padding: 24 }}
    >
      <div
        ref={modalBodyRef}
        style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}
      >
        <Table
          columns={columns}
          dataSource={cards}
          loading={loading}
          rowKey={(record) => record.id}
          scroll={{ x: 1200 }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          bordered
          size="middle"
          locale={{ emptyText: 'Không có thẻ nào.' }}
        />
      </div>
    </Modal>
  );
};

export default NoScheduleCardModal;
